import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createCourseResource,
  deleteCourseResource,
  getCourseResourceUrl,
  getCourseResources,
  updateCourseResource,
  uploadCourseResource,
} from '@/lib/api';
import { formatFileSize, materialKind } from '@/lib/lessonMedia';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Archive,
  Download,
  Eye,
  EyeOff,
  File,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Link2,
  Loader2,
  Music,
  Paperclip,
  Plus,
  Presentation,
  Trash2,
  Upload,
} from 'lucide-react';

const KIND_STYLES = {
  pdf: { Icon: FileText, tone: 'text-rose-600 bg-rose-50' },
  doc: { Icon: FileText, tone: 'text-blue-600 bg-blue-50' },
  sheet: { Icon: FileSpreadsheet, tone: 'text-emerald-600 bg-emerald-50' },
  slides: { Icon: Presentation, tone: 'text-amber-600 bg-amber-50' },
  image: { Icon: ImageIcon, tone: 'text-purple-600 bg-purple-50' },
  audio: { Icon: Music, tone: 'text-indigo-600 bg-indigo-50' },
  archive: { Icon: Archive, tone: 'text-slate-600 bg-slate-100' },
  file: { Icon: File, tone: 'text-[#062B1A]/60 bg-[#F7F8FA]' },
  link: { Icon: Link2, tone: 'text-sky-600 bg-sky-50' },
};

const UPLOAD_ACCEPT = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.txt', '.csv', '.zip', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.mp3', '.m4a',
].join(',');

function iconFor(resource) {
  if (resource.kind === 'link') return KIND_STYLES.link;
  return KIND_STYLES[materialKind(resource.content_type)] || KIND_STYLES.file;
}

function subtitleFor(resource) {
  if (resource.kind === 'link') return resource.url;
  const size = formatFileSize(resource.size);
  return `${resource.filename || 'File'}${size ? ` • ${size}` : ''}`;
}

/**
 * The materials attached to a course, or to one assignment within it.
 *
 * Read-only for students; passing `editable` turns it into the instructor's
 * upload-and-publish surface. Files live in a private bucket, so opening one is
 * a two-step exchange: ask the API for a short-lived presigned URL, then open
 * it. Draft resources are filtered out server-side for students, so an
 * instructor can stage next week's handout without leaking it.
 */
export default function CourseResources({
  courseId,
  itemId = '',
  assignmentId = '',
  editable = false,
  emptyLabel,
  compact = false,
}) {
  // One classwork item owns its attachments; an empty owner means the
  // course-level shelf. `assignmentId` is the original name for the same thing.
  const owner = itemId || assignmentId;
  const fileInputRef = useRef(null);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [uploadPercent, setUploadPercent] = useState(null);
  const [linkDraft, setLinkDraft] = useState({ open: false, title: '', url: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getCourseResources(courseId, owner ? { item_id: owner } : undefined);
      // The course-level shelf must not also show every item's attachments.
      const rows = data || [];
      setResources(owner ? rows : rows.filter((item) => !(item.item_id || item.assignment_id)));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not load course materials');
    } finally {
      setLoading(false);
    }
  }, [owner, courseId]);

  useEffect(() => {
    if (courseId) load();
  }, [courseId, load]);

  const handleOpen = async (resource) => {
    try {
      setBusyId(resource.id);
      const { data } = await getCourseResourceUrl(courseId, resource.id);
      if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
      else toast.error('That link could not be opened.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'That link could not be opened.');
    } finally {
      setBusyId('');
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setUploadPercent(0);
      await uploadCourseResource(courseId, file, {
        itemId: owner,
        // An assignment brief is only useful alongside a published assignment,
        // so attachments go straight out; course materials do too, and the
        // instructor can unpublish either.
        status: 'published',
        onProgress: setUploadPercent,
      });
      toast.success(`${file.name} uploaded`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploadPercent(null);
    }
  };

  const handleAddLink = async () => {
    const url = linkDraft.url.trim();
    if (!url) {
      toast.error('Paste a link first');
      return;
    }
    try {
      setBusyId('new-link');
      await createCourseResource(courseId, {
        kind: 'link',
        url,
        title: linkDraft.title.trim(),
        item_id: owner,
        status: 'published',
      });
      toast.success('Link added');
      setLinkDraft({ open: false, title: '', url: '' });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add that link');
    } finally {
      setBusyId('');
    }
  };

  const handleToggleVisibility = async (resource) => {
    const next = resource.status === 'published' ? 'draft' : 'published';
    try {
      setBusyId(resource.id);
      const { data } = await updateCourseResource(courseId, resource.id, { status: next });
      setResources((prev) => prev.map((item) => (item.id === resource.id ? data : item)));
      toast.success(next === 'published' ? 'Visible to students' : 'Hidden from students');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update visibility');
    } finally {
      setBusyId('');
    }
  };

  const handleDelete = async (resource) => {
    try {
      setBusyId(resource.id);
      await deleteCourseResource(courseId, resource.id);
      setResources((prev) => prev.filter((item) => item.id !== resource.id));
      toast.success('Removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove that material');
    } finally {
      setBusyId('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-5 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="course-resources">
      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={UPLOAD_ACCEPT}
            className="hidden"
            onChange={handleUpload}
            data-testid="course-resource-file-input"
          />
          <Button
            type="button"
            size="sm"
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            disabled={uploadPercent !== null}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mr-2 size-4" />
            Upload file
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-[#062B1A]/15"
            onClick={() => setLinkDraft((prev) => ({ ...prev, open: !prev.open }))}
          >
            <Link2 className="mr-2 size-4" />
            Add link
          </Button>
          <span className="text-xs text-[#062B1A]/40">PDF, Office, images, audio or ZIP · up to 50 MB</span>
        </div>
      )}

      {uploadPercent !== null && (
        <div className="space-y-1">
          <Progress value={uploadPercent} className="h-2" />
          <p className="text-xs text-[#062B1A]/45">Uploading… {uploadPercent}%</p>
        </div>
      )}

      {editable && linkDraft.open && (
        <div className="grid gap-2 rounded-lg border border-[#062B1A]/10 bg-[#F7F8FA] p-3 sm:grid-cols-[1fr_2fr_auto]">
          <Input
            value={linkDraft.title}
            onChange={(event) => setLinkDraft((prev) => ({ ...prev, title: event.target.value }))}
            placeholder="Label (optional)"
            aria-label="Link label"
          />
          <Input
            value={linkDraft.url}
            onChange={(event) => setLinkDraft((prev) => ({ ...prev, url: event.target.value }))}
            placeholder="https://..."
            aria-label="Link URL"
          />
          <Button
            type="button"
            className="bg-[#D4AF37] text-black hover:bg-[#B8941F]"
            disabled={busyId === 'new-link'}
            onClick={handleAddLink}
          >
            <Plus className="mr-1 size-4" />
            Add
          </Button>
        </div>
      )}

      {resources.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <Paperclip className="size-8 text-[#062B1A]/15" />
          <p className="text-sm text-[#062B1A]/50">
            {emptyLabel || (editable
              ? 'Nothing shared yet. Upload a file or add a link to get started.'
              : 'No materials have been shared yet.')}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {resources.map((resource) => {
            const { Icon, tone } = iconFor(resource);
            const hidden = resource.status !== 'published';
            return (
              <li
                key={resource.id}
                className={`flex items-center gap-3 rounded-lg border p-3 transition-colors hover:border-[#D4AF37]/40 ${
                  hidden ? 'border-dashed border-[#062B1A]/20 bg-[#F7F8FA]' : 'border-[rgba(6,43,26,0.08)] bg-white'
                }`}
              >
                <span className={`flex size-9 flex-shrink-0 items-center justify-center rounded-lg ${tone}`}>
                  <Icon className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium text-[#062B1A]">
                      {resource.title || resource.filename || resource.url}
                    </p>
                    {editable && hidden && (
                      <Badge variant="outline" className="shrink-0 text-[10px]">Hidden</Badge>
                    )}
                  </div>
                  <p className="truncate text-xs text-[#062B1A]/45">{subtitleFor(resource)}</p>
                  {!compact && resource.description && (
                    <p className="mt-1 text-xs text-[#062B1A]/55">{resource.description}</p>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="flex-shrink-0 border-[rgba(6,43,26,0.15)]"
                  disabled={busyId === resource.id}
                  onClick={() => handleOpen(resource)}
                  data-testid={`open-resource-${resource.id}`}
                >
                  {busyId === resource.id ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : resource.kind === 'link' ? (
                    <Link2 className="size-3.5" />
                  ) : (
                    <Download className="size-3.5" />
                  )}
                  <span className="ml-1.5 hidden sm:inline">{resource.kind === 'link' ? 'Open' : 'Download'}</span>
                </Button>
                {editable && (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="flex-shrink-0 text-[#062B1A]/60"
                      disabled={busyId === resource.id}
                      onClick={() => handleToggleVisibility(resource)}
                      aria-label={hidden ? `Publish ${resource.title}` : `Hide ${resource.title}`}
                    >
                      {hidden ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="flex-shrink-0 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      disabled={busyId === resource.id}
                      onClick={() => handleDelete(resource)}
                      aria-label={`Remove ${resource.title}`}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
